import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth, getDefaultRouteForUser, normalizeUserRole } from "@/lib/auth";

// Pages — Phase 1
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ClientDashboard from "./pages/ClientDashboard";
import ClientPlans from "./pages/ClientPlans";
import ClientRequest from "./pages/ClientRequest";
import ClientMessages from "./pages/ClientMessages";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPlans from "./pages/AdminPlans";
import AdminRequests from "./pages/AdminRequests";
import AdminDocuments from "./pages/AdminDocuments";
import AdminMessages from "./pages/AdminMessages";
import AdminPayments from "./pages/AdminPayments";
import AdminUsers from "./pages/AdminUsers";
import AdminOffers from "./pages/AdminOffers";
import AdminSettings from "./pages/AdminSettings";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import FaqPage from "./pages/FaqPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/Forgotpasswordpage";
import ResetPasswordPage from "./pages/Resetpasswordpage";
import EmailVerificationWall from "@/components/EmailVerificationWall";
import PaymentCallbackPage from "./pages/PaymentCallbackPage";

// Pages — Phase 2
import AIFilingPage from "./pages/AIFilingPage";
import AdminAIFiling from "./pages/AdminAIFiling";

import HowToFileITR from "./pages/HowToFileITR";
import OldVsNewTaxRegimePage from "./pages/OldVsNewTaxRegimePage";
import ITRFilingForSalariedPage from "./pages/ITRFilingForSalariedPage";

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, flagLoading } = useAuth();

  if (loading || flagLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (user.user_type === "client" && user.is_email_verified === false) {
    return <EmailVerificationWall />;
  }

  const role = normalizeUserRole(user.user_type);
  if (allowedRoles && !allowedRoles.map(normalizeUserRole).includes(role)) {
    return <Navigate to={getDefaultRouteForUser(user)} replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/payment/callback" element={<PaymentCallbackPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faqs" element={<FaqPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund-policy" element={<RefundPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/how-to-file-itr-using-form-16" element={<HowToFileITR />} />
          <Route path="/old-vs-new-tax-regime" element={<OldVsNewTaxRegimePage />} />
          <Route path="/itr-filing-for-salaried-employees" element={<ITRFilingForSalariedPage />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plans"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientPlans />
              </ProtectedRoute>
            }
          />
          <Route
            path="/request/:requestId"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientRequest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-filing"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <AIFilingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/plans"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminPlans />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/requests"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/documents"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminPayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/offers"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminOffers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-filing"
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminAIFiling />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;